import { ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  apiHealthScores,
  subjectTitle,
  subjectDetail,
  NOT_MEASURED_COPY,
  type HealthBand,
  type HealthScore,
} from '@/components/dashboard/healthApi';

/**
 * A row of health scores above the widgets, so an owner can see where to look
 * before reading a single figure.
 *
 * Two rules this component exists to hold:
 *
 *   - A score with no explanation is decoration. Every card opens a panel that
 *     shows the subtraction, names the findings that cost the points, and links
 *     to each one.
 *   - "Not measured" is rendered as its own state, never as 0% and never as
 *     100%. A subject nobody watches is not healthy; a subject with no data is
 *     not failing. Both are gaps, and both read as gaps.
 *
 * The colours are the Anomalies page's SEVERITY_STYLE map, reused rather than
 * re-picked. The polarity is inverted and that is the thing to keep straight:
 * a LOW-concern subject is the healthy one, CRITICAL is the one to open.
 */

/** Copied deliberately from pages/Anomalies.tsx:92-97 — same four colours. */
const BAND_STYLE: Record<HealthBand, string> = {
  LOW:      'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700',
  MEDIUM:   'bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-200 dark:border-sky-800',
  HIGH:     'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800',
  CRITICAL: 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-200 dark:border-red-800',
};

const BAND_WORD: Record<HealthBand, string> = {
  LOW: 'Healthy',
  MEDIUM: 'Watch',
  HIGH: 'Needs attention',
  CRITICAL: 'Urgent',
};

const NOT_MEASURED_STYLE =
  'bg-transparent text-muted-foreground border-dashed border-border';

function fmtDay(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return iso.slice(0, 10);
}

export default function HealthScoreRow() {
  const navigate = useNavigate();
  const [items, setItems] = useState<HealthScore[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<HealthScore | null>(null);
  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      return localStorage.getItem('signals.healthRow.expanded') === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('signals.healthRow.expanded', expanded ? '1' : '0');
    } catch {
      /* private window, blocked storage - the default is fine */
    }
  }, [expanded]);

  useEffect(() => {
    let live = true;
    apiHealthScores()
      .then((r) => { if (live) setItems(r.items); })
      // The row is an aid, not the page. A tenant that has never run the batch,
      // or one whose flag is off, must not turn the dashboard into an error.
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, []);

  const openAnomaly = useCallback((id: number) => {
    navigate(`/anomalies?anomaly=${id}`);
  }, [navigate]);

  if (failed || !items || items.length === 0) return null;

  // Folded by default. This row is about whether the DATA can be trusted, not
  // about the business — and it was the first thing on a page someone opened to
  // read their numbers, twelve tiles of it. One line keeps the signal (and the
  // count, which is the part that would make you look) without spending the top
  // of the page on it. The choice is remembered, so anyone who does want it open
  // sets it once.
  const urgent = items.filter((s) => s.band === 'CRITICAL').length;
  const watch = items.filter((s) => s.band === 'HIGH' || s.band === 'MEDIUM').length;

  return (
    <>
      <div className="mb-4">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
          aria-expanded={expanded}
        >
          <ChevronRight
            className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
          <span className="font-medium">Data health</span>
          {/* Subjects, not a finding total. Summing open findings gives 112 while
              the Anomalies tab beside it reads 38 -- both are right (the tab
              applies the owner's money floor, this counts every NEW and SEEN
              row) but two totals for "how much is wrong" on one screen is a
              contradiction to the person reading it. The per-subject counts are
              still on the cards, where they rank something. */}
          {urgent + watch > 0 ? (
            <span>
              {urgent > 0 ? `${urgent} subject${urgent === 1 ? '' : 's'} urgent` : ''}
              {urgent > 0 && watch > 0 ? ' · ' : ''}
              {watch > 0 ? `${watch} to watch` : ''}
              {` · ${items.length} scored`}
            </span>
          ) : (
            <span>Nothing needs attention across {items.length} subjects</span>
          )}
        </button>

        {!expanded ? null : (
        <div className="flex gap-2 flex-wrap mt-2">
          {items.map((s) => {
            const measured = s.not_measured === null && s.score !== null;
            const style = measured && s.band ? BAND_STYLE[s.band] : NOT_MEASURED_STYLE;
              const openCount = s.inputs.findings?.open_count ?? 0;
              const staleDays = s.inputs.freshness?.stale_days ?? null;
              const graceDays = s.inputs.freshness?.grace_days ?? 1;
              const isStale = staleDays !== null && staleDays > graceDays;
              return (
              <button
                key={s.subject}
                onClick={() => setOpen(s)}
                title="Open to see what this is based on"
                className={`group rounded-lg border px-3 py-2 text-left transition-colors hover:brightness-95 min-w-[9.5rem] ${style}`}
              >
                <div className="flex items-center gap-1">
                  <div className="text-xs font-medium truncate max-w-[11rem]">
                    {subjectTitle(s)}
                  </div>
                  {/* The card was a dead-looking tile, so nobody discovered that
                      the whole explanation sits one click away. */}
                  <ChevronRight className="h-3 w-3 shrink-0 opacity-40 group-hover:opacity-90 transition-opacity" />
                </div>
                {measured ? (
                  <>
                    {/* The count leads, not the score. "40" is a number out of a
                        scale nobody was told about, and the first question it
                        provokes is "40 of what?". "10 to review" is the same
                        judgement expressed as the thing the reader can act on. */}
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-xl font-semibold tabular-nums">
                        {openCount > 0 ? openCount : '\u2014'}
                      </span>
                      <span className="text-[11px] opacity-80">
                        {openCount > 0 ? 'to review' : 'nothing open'}
                      </span>
                    </div>
                    <div className="text-[10.5px] opacity-70 truncate max-w-[11rem]">
                      {BAND_WORD[s.band!]}
                      {isStale ? ` \u00b7 ${staleDays}d behind` : ''}
                    </div>
                  </>
                ) : (
                  <div className="text-sm font-medium opacity-80">Not measured</div>
                )}
              </button>
              );
          })}
        </div>
        )}
        {expanded && (
          <p className="text-[11px] text-muted-foreground/70 mt-2">
            Scored nightly over the last 30 days, so it does not follow the period
            below. Open a subject to see what its score is made of.
          </p>
        )}
      </div>

      <Sheet open={!!open} onOpenChange={(o) => { if (!o) setOpen(null); }}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {open && <HealthPanel score={open} onOpenAnomaly={openAnomaly} />}
        </SheetContent>
      </Sheet>
    </>
  );
}

/**
 * One sentence saying what this subject needs, in the reader's terms.
 *
 * Deliberately not the score. An owner opening this panel is asking "is there
 * anything here for me", and the answer is a number of findings and whether the
 * data arrived - not a position on a scale they were never shown.
 */
function headlineFor(score: HealthScore): string {
  const open = score.inputs.findings?.open_count ?? 0;
  const stale = score.inputs.freshness?.stale_days ?? null;
  const grace = score.inputs.freshness?.grace_days ?? 1;
  const parts: string[] = [];

  if (open > 0) {
    parts.push(
      open === 1
        ? '1 finding is open and has not been reviewed'
        : `${open} findings are open and have not been reviewed`
    );
  }
  if (stale !== null && stale > grace) {
    parts.push(`data is ${stale} days behind`);
  }
  if (score.inputs.quality?.blocked_columns?.length) {
    const n = score.inputs.quality.blocked_columns.length;
    parts.push(`${n} column${n === 1 ? '' : 's'} could not be trusted`);
  }
  if (parts.length === 0) {
    return 'Nothing needs attention here: no open findings, data is current.';
  }
  return parts.join('; ') + '.';
}

function HealthPanel({
  score, onOpenAnomaly,
}: { score: HealthScore; onOpenAnomaly: (id: number) => void }) {
  const { inputs } = score;
  const measured = score.not_measured === null && score.score !== null;

  return (
    <>
      <SheetHeader>
        <SheetTitle>{subjectTitle(score)}</SheetTitle>
        {/* Lead with the thing to do about it. The old first line was
            "Scored 40 of 100", which answers a question nobody asked and
            raises one nobody can answer without the formula. */}
        <SheetDescription>
          {measured ? headlineFor(score) : NOT_MEASURED_COPY[score.not_measured!]}
        </SheetDescription>
      </SheetHeader>

      {!measured ? (
        <p className="mt-4 text-sm text-muted-foreground">
          {score.not_measured === 'no_rules'
            ? 'Nothing is watching this subject, so there is nothing to score. Add an anomaly rule for it and it will be scored on the next nightly run.'
            : 'This subject holds no data inside the scored window, so no score can be defended. That is usually a feed that stopped rather than a business that stopped.'}
        </p>
      ) : (
        <div className="mt-4 space-y-5">
          {/* The subtraction, before any of the detail. Someone checking the
              number by hand should find it in the first thing they read. */}
          {inputs.arithmetic && (
            <div className="rounded-lg border bg-muted/40 px-3 py-2">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                Attention score — {score.score} out of 100
              </p>
              {/* The scale has to be stated before the subtraction, or the
                  reader is left working out what 100 and 60 were. */}
              <p className="text-xs text-muted-foreground mb-2">
                Every subject starts at 100 and loses points for open findings
                (up to 60), data that stopped arriving (up to 25) and columns the
                nightly check could not trust (up to 15). Lower means look here
                first.
              </p>
              <p className="text-sm font-mono">{inputs.arithmetic}</p>
            </div>
          )}

          <Component
            title="Open findings"
            points={inputs.findings?.points ?? 0}
            cap={inputs.findings?.cap}
            measured={inputs.findings?.measured ?? true}
            note={
              inputs.findings?.capped
                ? `${inputs.findings.open_count} open — past the ${inputs.findings.cap}-point ceiling, so the score understates it`
                : `${inputs.findings?.open_count ?? 0} open`
            }
          />

          <Component
            title="Data freshness"
            points={inputs.freshness?.points ?? 0}
            cap={inputs.freshness?.cap}
            measured={inputs.freshness?.measured ?? false}
            unmeasuredNote="No feed record for this subject, so freshness could not be checked."
            note={
              inputs.freshness?.stale_days === null || inputs.freshness?.stale_days === undefined
                ? null
                : inputs.freshness.stale_days <= (inputs.freshness.grace_days ?? 1)
                  ? `Up to date — last loaded ${fmtDay(inputs.freshness.last_update_date)}`
                  : `${inputs.freshness.stale_days} days behind — last loaded ${fmtDay(inputs.freshness.last_update_date)}`
            }
          />

          <Component
            title="Column trust"
            points={inputs.quality?.points ?? 0}
            cap={inputs.quality?.cap}
            measured={inputs.quality?.measured ?? false}
            unmeasuredNote="The nightly data-quality check has never run for this tenant, so nothing is known either way."
            note={
              inputs.quality?.blocked_columns?.length
                ? `${inputs.quality.blocked_columns.length} column(s) flagged`
                : 'No columns flagged'
            }
          >
            {inputs.quality?.blocked_columns?.map((c) => (
              <p key={c.column} className="text-xs text-muted-foreground mt-1">
                {c.reason}
              </p>
            ))}
          </Component>

          {inputs.data_presence === 'unverified' && (
            <p className="text-xs text-muted-foreground">
              This subject&apos;s data could not be verified on the last run — the score
              stands, but it was not checked against the table&apos;s date range.
            </p>
          )}

          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
              What pulled it down
            </p>
            {score.scoped ? (
              <p className="text-sm text-muted-foreground">
                The findings behind this score include alerts outside your own, so they
                are not listed here.
              </p>
            ) : score.findings.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nothing open. Any points lost came from freshness or column trust above.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {score.findings.map((f) => (
                  <li key={f.anomaly_id}>
                    <button
                      onClick={() => onOpenAnomaly(f.anomaly_id)}
                      className="w-full text-left rounded-md border px-3 py-2 hover:bg-accent transition-colors"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm truncate">
                          {f.metric_name ?? `Anomaly #${f.anomaly_id}`}
                        </span>
                        <span className="text-sm font-medium tabular-nums shrink-0">
                          −{f.points}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {f.severity} ({f.severity_points})
                        {f.impact_share !== null
                          ? ` × ${f.impact_multiplier} for ${(f.impact_share * 100).toFixed(1)}% of a month`
                          : ' × 1 — no money scale for this metric'}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {subjectDetail(score) && (
            /* The tenant's own description of this data. It is written for the
               model -- column names, which figure to quote -- so it is folded
               away rather than deleted: useful to whoever asks, noise to
               everyone else, and it used to be the first thing in the panel. */
            <details className="rounded-lg border bg-muted/20 px-3 py-2">
              <summary className="text-[11px] uppercase tracking-wide text-muted-foreground cursor-pointer select-none">
                What this data is
              </summary>
              <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                {subjectDetail(score)}
              </p>
            </details>
          )}

          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
              What would raise it
            </p>
            <ul className="text-sm text-muted-foreground list-disc pl-4 space-y-0.5">
              {(inputs.findings?.points ?? 0) > 0 && (
                <li>
                  Resolving the open findings above returns up to{' '}
                  {inputs.findings!.points} points.
                </li>
              )}
              {(inputs.freshness?.points ?? 0) > 0 && (
                <li>
                  Getting this feed loading again returns {inputs.freshness!.points} points.
                </li>
              )}
              {(inputs.quality?.points ?? 0) > 0 && (
                <li>
                  Fixing the flagged column(s) at the source returns{' '}
                  {inputs.quality!.points} points.
                </li>
              )}
              {(inputs.findings?.points ?? 0) === 0
                && (inputs.freshness?.points ?? 0) === 0
                && (inputs.quality?.points ?? 0) === 0 && (
                <li>Nothing — this subject is already at 100.</li>
              )}
            </ul>
          </div>

          {score.computed_at && (
            <p className="text-xs text-muted-foreground">
              Computed {score.computed_at.slice(0, 16).replace('T', ' ')} UTC by the
              nightly run.
            </p>
          )}
        </div>
      )}
    </>
  );
}

function Component({
  title, points, cap, measured, note, unmeasuredNote, children,
}: {
  title: string;
  points: number;
  cap?: number;
  measured: boolean;
  note?: string | null;
  unmeasuredNote?: string;
  children?: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium">{title}</p>
        {measured ? (
          <span className="text-sm tabular-nums">
            {points > 0 ? `−${points}` : '0'}
            {cap ? <span className="text-muted-foreground"> / {cap} max</span> : null}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">not measured</span>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {measured ? note : unmeasuredNote}
      </p>
      {children}
    </div>
  );
}
